import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

// Single source of truth for the Awards search state - encoded entirely in the
// URL query string so the persistent search shell survives refresh, deep-links
// and the Back button. Defaults are omitted from the URL to keep it tidy; the
// getter falls back to them. Consumers read `params` and write via setParam /
// setParams / reset; AwardsResults derives its RTK filterCriteria from `params`.
export const SEARCH_DEFAULTS = {
  q: "", // free-text term (Awards tab: award name)
  fn: "", // Awardees tab: first-name filter
  ln: "", // Awardees tab: last-name filter
  type: "awards", // active tab: "awards" | "awardees"
  aau: false, // honorific (AAU highly prestigious) quick-filter
  uta: false, // UTA-faculty awards quick-filter (UTA network only)
  cat: "", // Category facet
  level: "", // Level facet
  kind: "", // Kind facet (curated bucket)
  org: "", // Organization facet
  acad: "", // Academy facet (Awardees tab: NAM/NAE/NAS/NAI/AAS)
  year: "", // Year facet (Awardees tab: award-year span covers this year)
  page: 1, // 1-based page number
  size: 25, // page size (matches the 10/25/50/100 selector)
  sort: "", // sort column (table field name)
  dir: "", // sort direction: "asc" | "desc"
  seed: "", // Awardees default view: shuffle seed, so paging keeps one order
  adv: false, // is the Advanced panel open
};

const BOOL_KEYS = new Set(["aau", "uta", "adv"]);
const NUM_KEYS = new Set(["page", "size"]);

const parseValue = (key, raw) => {
  if (raw === null) return SEARCH_DEFAULTS[key];
  if (BOOL_KEYS.has(key)) return raw === "1" || raw === "true";
  if (NUM_KEYS.has(key)) {
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : SEARCH_DEFAULTS[key];
  }
  return raw;
};

const serializeValue = (key, value) => {
  // A value equal to its default is dropped from the URL.
  if (value === SEARCH_DEFAULTS[key]) return null;
  if (BOOL_KEYS.has(key)) return value ? "1" : null;
  if (value === "" || value === null || value === undefined) return null;
  return String(value);
};

export default function useAwardSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const params = useMemo(() => {
    const out = {};
    for (const key of Object.keys(SEARCH_DEFAULTS)) {
      out[key] = parseValue(key, searchParams.get(key));
    }
    return out;
  }, [searchParams]);

  // Apply a patch of { key: value }. Keys at their default drop out of the URL.
  // `replace: true` avoids stacking a history entry per keystroke while typing.
  const setParams = useCallback(
    (patch, { replace = false } = {}) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        const serialized = serializeValue(key, value);
        if (serialized === null) next.delete(key);
        else next.set(key, serialized);
      }
      if (pathname === "/awards") {
        setSearchParams(next, { replace });
      } else {
        // Searching/filtering from a detail page returns to the results view.
        const search = next.toString();
        navigate(
          { pathname: "/awards", search: search ? `?${search}` : "" },
          { replace }
        );
      }
    },
    [searchParams, setSearchParams, navigate, pathname]
  );

  const setParam = useCallback(
    (key, value, opts) => setParams({ [key]: value }, opts),
    [setParams]
  );

  // Clear the query + facets but keep the active tab (awards/people is a
  // navigation signal the user chose, not a filter). Returns to page 1.
  const reset = useCallback(
    (opts) =>
      setParams(
        {
          q: "",
          fn: "",
          ln: "",
          aau: false,
          uta: false,
          cat: "",
          level: "",
          kind: "",
          org: "",
          acad: "",
          year: "",
          sort: "",
          dir: "",
          page: 1,
          adv: false,
        },
        // `seed` survives a reset on purpose: clearing the search returns to the default view, and a new seed there would reshuffle under the user.
        opts
      ),
    [setParams]
  );

  // Count of active advanced facets - handy for a badge on the Advanced toggle.
  const activeFacetCount = useMemo(
    () =>
      ["cat", "level", "kind", "org", "acad", "year"].filter((k) => params[k])
        .length,
    [params]
  );

  return { params, setParam, setParams, reset, activeFacetCount };
}
